import { createTheme } from '@mui/material/styles';

// Premium Color Palette
export const colors = {
  primary: {
    deepGreen: '#005b2e',
    indigoBlue: '#003399',
    white: '#ffffff',
    softGranite: '#f4f6f8',
  },
  accent: {
    gradientStart: '#00b894',
    gradientEnd: '#0066cc',
  },
  semantic: {
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6',
  },
  text: {
    primary: '#1f2937',
    secondary: '#6b7280',
    disabled: '#9ca3af',
  },
};

// Custom MUI Theme
export const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary.deepGreen,
      light: '#00854a',
      dark: '#003d1f',
      contrastText: colors.primary.white,
    },
    secondary: {
      main: colors.primary.indigoBlue,
      light: '#0052cc',
      dark: '#002266',
      contrastText: colors.primary.white,
    },
    background: {
      default: colors.primary.softGranite,
      paper: colors.primary.white,
    },
    success: {
      main: colors.semantic.success,
    },
    warning: {
      main: colors.semantic.warning,
    },
    error: {
      main: colors.semantic.error,
    },
    info: {
      main: colors.semantic.info,
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Merriweather", "Georgia", serif',
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: '"Merriweather", "Georgia", serif',
      fontWeight: 700,
      fontSize: '2.5rem',
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: '"Merriweather", "Georgia", serif',
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: '"Merriweather", "Georgia", serif',
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '1rem',
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.06)',
    '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
    '0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)',
    '0 20px 25px rgba(0,0,0,0.1), 0 10px 10px rgba(0,0,0,0.04)',
    '0 25px 50px rgba(0,0,0,0.12), 0 12px 18px rgba(0,0,0,0.08)',
    // ... additional shadows
    ...Array(19).fill('0 25px 50px rgba(0,0,0,0.15)'),
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 24px',
          boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${colors.accent.gradientStart} 0%, ${colors.accent.gradientEnd} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.accent.gradientStart} 0%, ${colors.accent.gradientEnd} 100%)`,
            filter: 'brightness(1.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 0 0 3px rgba(0,91,46,0.1)',
            },
            '&.Mui-focused': {
              boxShadow: '0 0 0 3px rgba(0,91,46,0.2)',
            },
          },
        },
      },
    },
  },
});

export default theme;
